import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Container,
  Group,
  Loader,
  Modal,
  Paper,
  PasswordInput,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title
} from '@mantine/core';
import { IconArrowLeft, IconBan, IconEdit, IconPlus, IconShield, IconUser } from '@tabler/icons-react';
import React from 'react';
import { Permission, UserRole } from '../../../server/src/models/User';
import { useUserManagement } from '../../hooks/useUserManagement';

// Component để hiển thị role của user với Badge
const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const color = role === UserRole.ADMIN ? 'red' : 'blue';
  const icon = role === UserRole.ADMIN ? <IconShield size={14} /> : <IconUser size={14} />;
  
  return (
    <Badge color={color} leftSection={icon}>
      {role}
    </Badge>
  );
};

// Component để hiển thị danh sách quyền
const PermissionsList: React.FC<{ permissions: { permission: Permission }[] }> = ({ permissions }) => {
  if (permissions.length === 0) {
    return <Text size="sm" c="dimmed"><IconBan size={14} /> No permissions</Text>;
  }
  
  return (
    <Stack gap={5}>
      {permissions.map(p => (
        <Badge key={p.permission} size="sm" variant="light">
          {p.permission}
        </Badge>
      ))}
    </Stack>
  );
};

// Component chính
const UserManagement: React.FC = () => {
  const {
    // State
    users,
    opened,
    selectedUser,
    error,
    success,
    isLoading,
    createModalOpened,
    createUserForm,
    
    // Actions
    handleEditUser,
    handleCloseModal,
    handleSaveUser,
    handlePermissionChange,
    handleCreateUser,
    navigateBack,
    setCreateModalOpened,
    clearError,
    clearSuccess
  } = useUserManagement();

  return (
    <Container size="lg" py="lg">
      <Paper p="md" mb="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group>
            <Button
              variant="subtle"
              size="sm"
              leftSection={<IconArrowLeft size={16} />}
              onClick={navigateBack}
            >
              Back to Dashboard
            </Button>
            <Title order={2}>User Management</Title>
          </Group>
          <Group>
            {isLoading && <Loader size="sm" />}
            <Button 
              leftSection={<IconPlus size={16} />}
              onClick={() => setCreateModalOpened(true)}
            >
              Create User
            </Button>
          </Group>
        </Group>

        {error && (
          <Alert color="red" title="Error" withCloseButton onClose={clearError} mb="md">
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="green" title="Success" withCloseButton onClose={clearSuccess} mb="md">
            {success}
          </Alert>
        )}

        <Paper withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Permissions</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.length === 0 && !isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={5} align="center">
                    <Text c="dimmed">No users found</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                users.map((user) => (
                  <Table.Tr key={user._id}>
                    <Table.Td>
                      <Text fw={500}>{user.name}</Text>
                    </Table.Td>
                    <Table.Td>{user.email}</Table.Td>
                    <Table.Td>
                      <RoleBadge role={user.role} />
                    </Table.Td>
                    <Table.Td>
                      <PermissionsList permissions={user.userPermissions} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon 
                        variant="filled" 
                        color="blue" 
                        onClick={() => handleEditUser(user)}
                        disabled={isLoading}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>
      </Paper>

      {/* Modal chỉnh sửa quyền user */}
      <Modal 
        opened={opened} 
        onClose={handleCloseModal}
        title={<Title order={3}>Edit User Permissions</Title>}
        size="md"
        centered
      >
        {selectedUser && (
          <Stack>
            <Group align="center">
              <Text fw={700}>{selectedUser.name}</Text>
              <Text c="dimmed" size="sm">({selectedUser.email})</Text>
              <RoleBadge role={selectedUser.role} />
            </Group>

            <Paper withBorder p="md" bg="gray.0">
              <Text fw={500} mb="xs">Permissions</Text>
              <SimpleGrid cols={2}>
                {Object.values(Permission).map((permission) => (
                  <Checkbox
                    key={permission}
                    label={permission}
                    checked={selectedUser.userPermissions.some(p => p.permission === permission)}
                    onChange={() => handlePermissionChange(permission)}
                    styles={{ label: { textTransform: 'capitalize' } }}
                  />
                ))}
              </SimpleGrid>
            </Paper>

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={handleCloseModal} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleSaveUser} loading={isLoading}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Modal tạo user mới */}
      <Modal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        title={<Title order={3}>Create New User</Title>}
        size="md"
        centered
      >
        <form onSubmit={createUserForm.onSubmit(handleCreateUser)}>
          <Stack>
            <TextInput
              required
              label="Name"
              placeholder="Enter full name"
              {...createUserForm.getInputProps('name')}
            />
            
            <TextInput
              required
              label="Email"
              placeholder="email@example.com"
              {...createUserForm.getInputProps('email')}
            />
            
            <PasswordInput
              required
              label="Password"
              placeholder="Minimum 6 characters"
              {...createUserForm.getInputProps('password')}
            />
            
            <Checkbox
              label="Create as Admin"
              {...createUserForm.getInputProps('isAdmin', { type: 'checkbox' })}
            />
            
            <Group justify="flex-end" mt="md">
              <Button 
                variant="light" 
                onClick={() => setCreateModalOpened(false)} 
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isLoading}>
                Create User
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
};

export default UserManagement; 